import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, ChevronRight, Menu, X } from 'lucide-react';
import { Logo } from './Logo';
import { GlobalSearch } from './GlobalSearch';
import { api } from '@/api';

/**
 * WPSL TV navigation — soccer-only, single-league (WPSL)
 * Header: red (#e61a32) bar, blue bottom border, white nav links
 */
export const Navigation = ({ leagues = [] }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [openSubregion, setOpenSubregion] = useState(null);
  const [liveCount, setLiveCount] = useState(0);
  const location = useLocation();

  // Group leagues by region slug so each region's flyout submenu lists its own conferences.
  const leaguesByRegion = React.useMemo(() => {
    const map = {};
    for (const l of leagues) {
      const key = (l.region || '').toLowerCase();
      if (!key) continue;
      (map[key] = map[key] || []).push(l);
    }
    return map;
  }, [leagues]);

  // Poll for live games so the nav can show a red pulsing dot. 60s interval matches LivePage.
  useEffect(() => {
    let cancelled = false;
    const refresh = () => {
      api.getLiveNowGames()
        .then(r => { if (!cancelled) setLiveCount((r.data || []).length); })
        .catch(() => {});
    };
    refresh();
    const id = setInterval(refresh, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // WPSL Regions (top-level grouping for teams/conferences)
  const REGIONS = [
    { slug: 'west', name: 'West' },
    { slug: 'east', name: 'East' },
    { slug: 'central', name: 'Central' },
    { slug: 'south', name: 'South' },
  ];

  const linkBase = "px-4 py-2 text-sm font-bold uppercase tracking-wide transition-colors text-white hover:text-white/70";
  const linkActive = "text-white border-b-2 border-white";

  return (
    <nav
      className="sticky top-0 z-50 border-b-2 border-wpsl-blue"
      style={{ backgroundColor: '#e61a32', fontFamily: 'Montserrat, sans-serif' }}
      data-testid="wpsl-nav"
    >
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <Logo className="h-[56px] w-auto" />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className={`${linkBase} flex items-center gap-2 ${location.pathname === '/' ? linkActive : ''}`}
              data-testid="nav-home"
            >
              <Home className="w-4 h-4" />
              Home
            </Link>

            <Link
              to="/live"
              className={`${linkBase} flex items-center gap-2 ${location.pathname === '/live' ? linkActive : ''}`}
              data-testid="nav-live"
              title={liveCount > 0 ? `${liveCount} game${liveCount === 1 ? '' : 's'} live now` : 'Live channel'}
            >
              <span className="relative inline-flex items-center justify-center">
                <span
                  className={`w-2 h-2 rounded-full ${liveCount > 0 ? 'bg-white animate-pulse' : 'bg-white/40'}`}
                  data-testid={liveCount > 0 ? 'nav-live-dot-active' : 'nav-live-dot-idle'}
                />
                {liveCount > 0 && (
                  <span className="absolute inline-flex w-3 h-3 rounded-full bg-white/40 animate-ping" />
                )}
              </span>
              Live
              {liveCount > 0 && (
                <span className="ml-1 text-[10px] font-bold bg-white/20 px-1.5 py-0.5 rounded">{liveCount}</span>
              )}
            </Link>

            {/* Regions dropdown — each region row has a flyout submenu listing
                conferences (leagues) in that region. */}
            <div
              className="relative"
              onMouseEnter={() => setOpenDropdown('regions')}
              onMouseLeave={() => { setOpenDropdown(null); setOpenSubregion(null); }}
            >
              <button
                className={`${linkBase} flex items-center gap-1`}
                data-testid="nav-regions"
              >
                Regions
                <ChevronRight className={`w-4 h-4 transition-transform ${openDropdown === 'regions' ? 'rotate-90' : ''}`} />
              </button>
              {openDropdown === 'regions' && (
                <div className="absolute top-full left-0 pt-2 z-50">
                  <div className="w-56 bg-gray-900 rounded-lg shadow-xl border border-gray-700 py-2">
                    {REGIONS.map(r => {
                      const conferences = leaguesByRegion[r.slug] || [];
                      return (
                        <div
                          key={r.slug}
                          className="relative"
                          onMouseEnter={() => setOpenSubregion(r.slug)}
                        >
                          <Link
                            to={`/region/${r.slug}`}
                            className="flex items-center justify-between px-3 py-2 text-sm text-gray-200 hover:bg-gray-800 hover:text-white transition-colors"
                            onClick={() => { setOpenDropdown(null); setOpenSubregion(null); }}
                            data-testid={`nav-region-${r.slug}`}
                          >
                            <span>{r.name}</span>
                            {conferences.length > 0 && (
                              <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
                            )}
                          </Link>
                          {openSubregion === r.slug && conferences.length > 0 && (
                            <div className="absolute top-0 left-full pl-2 z-50">
                              <div className="w-56 bg-gray-900 rounded-lg shadow-xl border border-gray-700 py-2">
                                <div className="px-3 pb-1 text-[10px] uppercase tracking-wider text-gray-500 font-bold">
                                  Conferences
                                </div>
                                {conferences.map(league => (
                                  <Link
                                    key={league.id}
                                    to={`/league/${league.slug}`}
                                    className="block px-3 py-2 text-sm text-gray-200 hover:bg-gray-800 hover:text-white transition-colors"
                                    onClick={() => { setOpenDropdown(null); setOpenSubregion(null); }}
                                    data-testid={`nav-conference-${league.slug}`}
                                  >
                                    {league.name}
                                  </Link>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Find a Stream dropdown — primary CTA + Partners as sub-item */}
            <div
              className="relative"
              onMouseEnter={() => setOpenDropdown('find')}
              onMouseLeave={() => setOpenDropdown(null)}
            >
              <Link
                to="/find-game"
                className={`${linkBase} flex items-center gap-1 ${(location.pathname === '/find-game' || location.pathname === '/partners') ? linkActive : ''}`}
                data-testid="nav-find-stream"
              >
                Find a Stream
                <ChevronRight className={`w-4 h-4 transition-transform ${openDropdown === 'find' ? 'rotate-90' : ''}`} />
              </Link>
              {openDropdown === 'find' && (
                <div className="absolute top-full left-0 pt-2 z-50">
                  <div className="w-56 bg-gray-900 rounded-lg shadow-xl border border-gray-700 py-2">
                    <Link
                      to="/find-game"
                      className="block px-3 py-2 text-sm text-gray-200 hover:bg-gray-800 hover:text-white transition-colors"
                      onClick={() => setOpenDropdown(null)}
                      data-testid="nav-find-stream-link"
                    >
                      Find a Stream
                    </Link>
                    <Link
                      to="/partners"
                      className="block px-3 py-2 text-sm text-gray-200 hover:bg-gray-800 hover:text-white transition-colors"
                      onClick={() => setOpenDropdown(null)}
                      data-testid="nav-partners"
                    >
                      Partners
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {/* Global search — inline on desktop */}
            <GlobalSearch variant="inline" />
          </div>

          {/* Mobile actions — search icon + hamburger */}
          <div className="flex items-center gap-1 md:hidden">
            <GlobalSearch variant="icon" />
            <button
              className="text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Nav */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-white/20 py-2" data-testid="mobile-nav">
            <Link to="/" className="block px-4 py-3 text-white font-bold uppercase tracking-wide" onClick={() => setMobileMenuOpen(false)}>
              Home
            </Link>
            <Link
              to="/live"
              className="flex items-center gap-2 px-4 py-3 text-white font-bold uppercase tracking-wide"
              onClick={() => setMobileMenuOpen(false)}
              data-testid="nav-live-mobile"
            >
              <span className={`w-2 h-2 rounded-full ${liveCount > 0 ? 'bg-white animate-pulse' : 'bg-white/40'}`} />
              Live
              {liveCount > 0 && (
                <span className="ml-auto text-[10px] font-bold bg-white/20 px-1.5 py-0.5 rounded">{liveCount}</span>
              )}
            </Link>
            <div className="px-4 py-2 text-white/60 text-xs uppercase tracking-wider">Regions</div>
            {REGIONS.map(r => {
              const conferences = leaguesByRegion[r.slug] || [];
              return (
                <div key={r.slug}>
                  <Link
                    to={`/region/${r.slug}`}
                    className="block px-6 py-2 text-white text-sm font-medium"
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid={`nav-region-${r.slug}-mobile`}
                  >
                    {r.name}
                  </Link>
                  {conferences.length > 0 && (
                    <div>
                      {conferences.map(league => (
                        <Link
                          key={league.id}
                          to={`/league/${league.slug}`}
                          className="block pl-10 pr-6 py-1.5 text-white/80 text-xs"
                          onClick={() => setMobileMenuOpen(false)}
                          data-testid={`nav-conference-${league.slug}-mobile`}
                        >
                          ↳ {league.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            <Link to="/find-game" className="block px-4 py-3 text-white font-bold uppercase tracking-wide border-t border-white/10" onClick={() => setMobileMenuOpen(false)}>
              Find a Stream
            </Link>
            <Link to="/partners" className="block pl-10 pr-4 py-2 text-white/80 text-sm" onClick={() => setMobileMenuOpen(false)}>
              ↳ Partners
            </Link>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
